import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_botanical_garden_005',
  name: 'Botanical Garden',
  occasionType: 'BABY_SHOWER',
  description: 'A fresh sage green and gold nature-inspired invitation decorated with delicate eucalyptus leaves and fine gold borders.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'botanical', 'sage-green', 'gold'],
  colorScheme: { primary: '#15803d', accent: '#a3e635', bg: '#f0fdf4' },
};

export function BotanicalGarden({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Olivia Meadows';
  const hostedBy = baby.hostedBy || data.hostName || 'The Meadows Family';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'November 22, 2026';
  let dayOfWeek = 'Sunday';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '11:30 AM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'The Conservatory Room';
  const venueAddress = baby.venueAddress || data.eventVenue || '101 Greenhouse Lane, Portland, OR';
  
  const rsvpName = baby.rsvpName || 'Madeline';
  const rsvpPhone = baby.rsvpPhone || '+1 (555) 018-7766';
  const message = baby.message || data.customMessage || 'We are growing! Join us for a botanical celebration to shower our mother-to-be.';
  const dressCode = baby.dressCode || '';

  return (
    <div className="min-h-screen bg-[#fafcfb] py-12 px-4 flex flex-col items-center justify-start font-sans relative overflow-hidden">
      {/* Import Google Fonts */}
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300..700;1,300..700&family=Alex+Brush&family=Lato:wght@300;400;700&display=swap');
        .font-serif-botanical { font-family: 'Cormorant Garamond', serif; }
        .font-cursive-botanical { font-family: 'Alex Brush', cursive; }
        .font-sans-botanical { font-family: 'Lato', sans-serif; }
        .gold-border-botanical {
          border-color: #d4af37;
        }
      `}} />

      {/* Decorative top leaves SVG */}
      <div className="absolute top-0 inset-x-0 h-48 opacity-20 pointer-events-none flex justify-center">
        <svg className="w-96 h-full" viewBox="0 0 400 150" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Leaf vine arch */}
          <path d="M50,10 Q200,80 350,10" stroke="#16a34a" strokeWidth="1.5" fill="none" />
          {/* Leaves */}
          <path d="M100,22 C90,12 110,8 115,20 C120,32 110,32 100,22 Z" fill="#15803d" />
          <path d="M150,38 C140,28 160,24 165,36 C170,48 160,48 150,38 Z" fill="#166534" />
          <path d="M200,45 C190,35 210,31 215,43 C220,55 210,55 200,45 Z" fill="#14532d" />
          <path d="M250,38 C240,28 260,24 265,36 C270,48 260,48 250,38 Z" fill="#166534" />
          <path d="M300,22 C290,12 310,8 315,20 C320,32 310,32 300,22 Z" fill="#15803d" />
        </svg>
      </div>

      {/* Main Card */}
      <div className="w-full max-w-[480px] aspect-[9/16] bg-white rounded-[24px] shadow-2xl p-8 relative flex flex-col justify-between border-[12px] border-[#f0fdf4] overflow-hidden">
        
        {/* Fine gold geometric frame */}
        <div className="absolute inset-2 border border-[#d4af37]/40 rounded-[16px] pointer-events-none"></div>
        <div className="absolute inset-3 border border-[#d4af37]/20 rounded-[14px] pointer-events-none"></div>
        
        {/* Diamond gold accent in corners */}
        <div className="absolute top-4 left-4 w-2 h-2 rotate-45 bg-[#d4af37]"></div>
        <div className="absolute top-4 right-4 w-2 h-2 rotate-45 bg-[#d4af37]"></div>
        <div className="absolute bottom-4 left-4 w-2 h-2 rotate-45 bg-[#d4af37]"></div>
        <div className="absolute bottom-4 right-4 w-2 h-2 rotate-45 bg-[#d4af37]"></div>

        {/* Content Container */}
        <div className="flex-grow flex flex-col justify-center items-center text-center relative z-10 py-2 px-2">
          
          <span className="font-sans-botanical text-[9px] tracking-[0.3em] text-[#16a34a] uppercase font-bold mb-3">
            Please Join Us As We Welcome
          </span>

          <h2 className="font-serif-botanical text-3xl md:text-4xl text-[#14532d] tracking-widest font-normal mb-1">
            BOTANICAL SHOWER
          </h2>

          <span className="font-sans-botanical text-xs text-stone-400 italic mb-6">
            celebrating
          </span>

          <h1 className="font-cursive-botanical text-5xl text-[#15803d] leading-none mb-6">
            {motherName}
          </h1>

          <div className="w-16 h-px bg-[#d4af37]/30 mb-6"></div>

          <p className="font-sans-botanical text-xs tracking-wider text-stone-500 mb-6 leading-relaxed">
            Hosted with love by <br />
            <span className="font-bold text-stone-800">{hostedBy}</span>
          </p>

          {/* Date & Time block */}
          <div className="flex flex-col items-center mb-6">
            <span className="font-serif-botanical text-xl text-[#14532d] font-bold">
              {dayOfWeek}, {formattedDate}
            </span>
            <span className="font-sans-botanical text-xs tracking-widest text-[#15803d] uppercase font-bold mt-1.5">
              At {eventTime}
            </span>
          </div>

          {/* Venue block */}
          <div className="flex flex-col items-center mb-6 max-w-[280px]">
            <span className="font-serif-botanical text-sm font-bold text-stone-800 mb-1">
              {venueName}
            </span>
            <span className="font-sans-botanical text-xs text-stone-500 leading-normal">
              {venueAddress}
            </span>
          </div>

          {/* Dress Code */}
          {dressCode && (
            <div className="mb-6 px-4 py-1.5 bg-[#f0fdf4] rounded-full border border-[#bbf7d0]">
              <span className="font-sans-botanical text-[10px] tracking-wider text-[#15803d] uppercase font-bold">
                Dress Code: {dressCode}
              </span>
            </div>
          )}

          {/* Message */}
          {message && (
            <p className="font-serif-botanical text-xs text-stone-500 italic leading-relaxed max-w-[300px] mb-6">
              "{message}"
            </p>
          )}

          {/* RSVP Button inside Card */}
          <div className="mt-2 w-full flex justify-center">
            <button
              type="button"
              onClick={() => setShowRsvpModal(true)}
              className="inline-flex items-center justify-center px-8 py-3 bg-[#14532d] hover:bg-[#0f3e21] text-white font-bold text-sm uppercase tracking-widest rounded-full shadow-md transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0"
            >
              RSVP Now
            </button>
          </div>

        </div>
      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          {/* Modal Container */}
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: BotanicalGarden });
